import { useState, useEffect, useRef, useCallback } from "react";
import { 
  Send, Bot, User, Loader2, Plus, Trash2, MessageSquare,
  Sparkles, Clock, ChevronRight, AlertCircle, Package, 
  Calendar, Factory, Lightbulb
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import axios from "axios";
import ChatMessageRenderer from "@/components/ChatMessageRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanningAgent = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch sessions on mount
  useEffect(() => {
    fetchSessions();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/agent/sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error("Failed to fetch sessions:", error);
    }
  };

  const fetchSessionMessages = async (sessionId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/agent/sessions/${sessionId}/messages`);
      setMessages(response.data);
      setCurrentSessionId(sessionId);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      toast.error("Failed to load conversation");
    } finally {
      setIsLoading(false);
    }
  };

  const startNewSession = () => {
    setCurrentSessionId(null);
    setMessages([]);
    setSuggestions([
      "What are my open sales orders?",
      "Show me material shortages",
      "Generate production schedule for next week",
      "Analyze capacity utilization"
    ]);
    inputRef.current?.focus();
  };

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isSending) return;

    const userMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsSending(true);
    setSuggestions([]);

    try {
      const response = await axios.post(`${API}/agent/chat`, {
        message: messageText,
        session_id: currentSessionId,
        context: {}
      });

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      setCurrentSessionId(response.data.session_id);
      setSuggestions(response.data.suggestions || []);

      // Refresh sessions list
      fetchSessions();

    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to get response from agent");
      
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
      setInputMessage(messageText);
    } finally {
      setIsSending(false);
    }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/agent/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        startNewSession();
      }
      toast.success("Session deleted");
    } catch (error) {
      console.error("Failed to delete session:", error);
      toast.error("Failed to delete session");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === "user";
    
    return (
      <div
        key={index}
        className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}
        data-testid={`message-${index}`}
      >
        {!isUser && (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Bot className="w-4 h-4 text-primary" />
          </div>
        )}
        
        <div className={`max-w-[80%] ${isUser ? "order-first" : ""}`}>
          <div
            className={`rounded-2xl px-4 py-3 ${
              isUser 
                ? "bg-primary text-primary-foreground rounded-tr-sm" 
                : "bg-secondary rounded-tl-sm"
            }`}
          >
            <div className="whitespace-pre-wrap text-sm leading-relaxed">
              <ChatMessageRenderer content={message.content} />
            </div>
          </div>
          <div className={`text-xs text-muted-foreground mt-1 ${isUser ? "text-right" : ""}`}>
            {formatTimestamp(message.timestamp)}
          </div>
        </div>
        
        {isUser && (
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-primary-foreground" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4" data-testid="planning-agent-page">
      {/* Sessions Sidebar */}
      <div className="w-72 flex-shrink-0">
        <Card className="h-full card-industrial">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Sessions
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={startNewSession}
                className="h-8"
                data-testid="new-session-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-240px)]">
              <div className="px-3 pb-3 space-y-2">
                {sessions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No conversations yet</p>
                    <p className="text-xs mt-1">Start a new session to begin</p>
                  </div>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.id}
                      className={`group flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                        currentSessionId === session.id 
                          ? "bg-primary/10 border border-primary/20" 
                          : "hover:bg-secondary"
                      }`}
                      onClick={() => fetchSessionMessages(session.id)}
                      data-testid={`session-${session.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{session.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {session.message_count} messages
                        </p>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => deleteSession(session.id, e)}
                      >
                        <Trash2 className="w-3 h-3 text-muted-foreground" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        <Card className="flex-1 card-industrial flex flex-col">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">Production Planning Agent</CardTitle>
                <p className="text-sm text-muted-foreground">
                  AI-powered assistant for scheduling and planning
                </p>
              </div>
              <Badge variant="secondary" className="ml-auto">
                <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 animate-pulse"></span>
                Online
              </Badge>
            </div>
          </CardHeader>
          
          <CardContent className="flex-1 p-0 flex flex-col">
            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <Skeleton className="h-20 flex-1 rounded-2xl" />
                    </div>
                  ))}
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Bot className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-medium mb-2">How can I help you today?</h3>
                  <p className="text-muted-foreground text-sm max-w-md mb-6">
                    I can help you analyze demand, plan production schedules, identify shortages, 
                    and optimize your manufacturing operations.
                  </p>
                  
                  {/* Quick Start Cards */}
                  <div className="grid grid-cols-2 gap-3 max-w-lg">
                    <button 
                      className="p-3 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors text-left"
                      onClick={() => sendMessage("What are my open sales orders for this week?")}
                      data-testid="quick-orders"
                    >
                      <Package className="w-5 h-5 text-primary mb-2" />
                      <p className="text-sm font-medium">View Orders</p>
                      <p className="text-xs text-muted-foreground">Check open sales orders</p>
                    </button>
                    <button 
                      className="p-3 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors text-left"
                      onClick={() => sendMessage("Are there any material shortages I should know about?")}
                      data-testid="quick-shortages"
                    >
                      <AlertCircle className="w-5 h-5 text-amber-500 mb-2" />
                      <p className="text-sm font-medium">Check Shortages</p>
                      <p className="text-xs text-muted-foreground">Find material issues</p>
                    </button>
                    <button 
                      className="p-3 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors text-left"
                      onClick={() => sendMessage("Generate an optimized production schedule for the next 2 weeks")}
                      data-testid="quick-schedule"
                    >
                      <Calendar className="w-5 h-5 text-blue-500 mb-2" />
                      <p className="text-sm font-medium">Plan Schedule</p>
                      <p className="text-xs text-muted-foreground">Generate production plan</p>
                    </button>
                    <button 
                      className="p-3 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors text-left"
                      onClick={() => sendMessage("Analyze current capacity utilization across all work centers")}
                      data-testid="quick-capacity"
                    >
                      <Factory className="w-5 h-5 text-emerald-500 mb-2" />
                      <p className="text-sm font-medium">Capacity</p>
                      <p className="text-xs text-muted-foreground">Check resource usage</p>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((msg, idx) => renderMessage(msg, idx))}
                  
                  {isSending && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="w-4 h-4 text-primary" />
                      </div>
                      <div className="bg-secondary rounded-2xl rounded-tl-sm px-4 py-3">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
            
            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div className="px-4 py-2 border-t bg-secondary/30">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <span className="text-xs text-muted-foreground">Suggestions</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      className="px-3 py-1.5 text-xs rounded-full bg-background border hover:bg-secondary transition-colors"
                      onClick={() => sendMessage(suggestion)}
                      data-testid={`suggestion-${idx}`}
                    >
                      {suggestion}
                      <ChevronRight className="w-3 h-3 inline ml-1" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Input Area */}
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  placeholder="Ask about production planning, schedules, or inventory..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isSending}
                  className="flex-1"
                  data-testid="chat-input"
                />
                <Button 
                  onClick={() => sendMessage()} 
                  disabled={!inputMessage.trim() || isSending}
                  data-testid="send-btn"
                >
                  {isSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Powered by GPT-5.2 • Press Enter to send
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PlanningAgent;
